import React, {useEffect} from 'react'
import {useField} from 'formik'
import {TComponentTextareaOptionsProps} from './_types'
import ComponentButton from '../ComponentButton'
import {useIntl} from 'react-intl'
import ComponentCheckbox from '../ComponentCheckbox'
import {useTextareaOptions} from './hook/useTextareaOptions'

const ComponentTextareaOptions: React.FC<TComponentTextareaOptionsProps> = (props) => {
    const {
        article,
        is_disabled,
        is_visible = true,
        settings = {},
        reloadKey,
        title,
        description,
    } = props
    const [field, meta] = useField<string>(article)
    const value = field.value
    const isError = Boolean(meta.error && meta.touched)
    const intl = useIntl()

    const logic = useTextareaOptions({
        article,
        settings,
        is_visible,
        reloadKey,
    })

    const openDrawerAnnounced = React.useCallback(() => {
        window.dispatchEvent(new CustomEvent('textareaOptions:open', {detail: {article}}))
        logic.openDrawer()
    }, [article, logic])

    // слушаем чужие открытия, чтобы закрыться
    useEffect(() => {
        const onSomeoneOpened = (ev: Event) => {
            const detail = (ev as CustomEvent).detail as {article?: string}
            if (detail?.article !== article) logic.setOpen(false)
        }
        window.addEventListener('textareaOptions:open', onSomeoneOpened as EventListener)
        return () =>
            window.removeEventListener('textareaOptions:open', onSomeoneOpened as EventListener)
    }, [article, logic])

    if (!is_visible) return null

    const resClass = `componentTextarea form-control form-control-solid${isError ? ' invalid' : ''}`

    return (
        <>
            <div
                ref={logic.rootRef}
                className={`componentTextareaOptions ${
                    logic.open ? 'componentTextareaOptions--open' : ''
                }`}
            >
                {/* ROW */}
                <div className='componentTextareaOptions__row'>
                    <textarea
                        ref={logic.taRef}
                        name={article}
                        className={resClass}
                        value={value}
                        rows={logic.rows}
                        placeholder={logic.placeholder ?? description ?? 'Введите текст…'}
                        onFocus={openDrawerAnnounced}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={is_disabled}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                logic.handleSubmit()
                            }
                        }}
                    />

                    <div className='componentTextareaOptions__side'>
                        <ComponentButton
                            className='componentTextareaOptions__createButton'
                            type='custom'
                            settings={{
                                title: logic.creating
                                    ? intl.formatMessage({id: "FIELD.SAVING"})
                                    : logic.created
                                    ? intl.formatMessage({id: "FIELD.SAVED"})
                                    : intl.formatMessage({id: "FIELD.CREATE_TEMPLATE"}),
                                background: 'primary-light',
                                icon: 'plus-circle',
                            }}
                            defaultLabel='icon'
                            loading={logic.creating}
                            disabled={logic.creating}
                            customHandler={() => logic.createTemplate(value)}
                        />

                        <div className='componentTextareaOptions__tools'>
                            <ComponentCheckbox
                                label={intl.formatMessage({id: 'FIELD.FOR_PRINT'})}
                                customChecked={logic.forPrint}
                                customHandler={() => logic.setForPrint((p) => !p)}
                                className='for-print-toggle'
                                buttonLike
                                hoverLabel
                            />
                        </div>
                    </div>
                </div>

                {/* ERROR */}
                {isError ? <div className='invalid_feedback'>{meta.error as string}</div> : null}
            </div>

            {/* OVERLAY */}
            <div
                className={`componentTextareaOptions__overlay ${
                    logic.open ? 'componentTextareaOptions__overlay--open' : ''
                }`}
                onClick={() => logic.setOpen(false)}
                style={{pointerEvents: 'none'}}
            />

            {/* DRAWER */}
            <aside
                ref={logic.drawerRef}
                className={`componentTextareaOptions__drawer ${
                    logic.open ? 'componentTextareaOptions__drawer--open' : ''
                }`}
                aria-hidden={!logic.open}
            >
                <div className='componentTextareaOptions__head'>
                    <input
                        className='componentTextareaOptions__search'
                        value={logic.search}
                        onChange={(e) => logic.setSearch(e.target.value)}
                        placeholder={intl.formatMessage({id: 'SEARCH.PLACEHOLDER'})}
                    />
                </div>

                <div className='componentTextareaOptions__list'>
                    {logic.isLoading && (
                        <div className='componentTextareaOptions__empty'>
                            <svg className='splash-spinner' viewBox='0 0 50 50'>
                                <circle
                                    className='path'
                                    cx='25'
                                    cy='25'
                                    r='20'
                                    fill='none'
                                    strokeWidth='5'
                                ></circle>
                            </svg>
                        </div>
                    )}
                    {!logic.isLoading && logic.filtered.length === 0 && (
                        <div className='componentTextareaOptions__empty'>
                            {intl.formatMessage({id: 'SEARCH.EMPTY_LIST'})}
                        </div>
                    )}
                    {!logic.isLoading &&
                        logic.filtered.map((o) => (
                            <button
                                key={String(o.value)}
                                type='button'
                                className='componentTextareaOptions__item'
                                onClick={() => logic.addText(o.label, value, article)}
                            >
                                {o.label}
                            </button>
                        ))}
                </div>
            </aside>
        </>
    )
}

export default ComponentTextareaOptions
